import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import axios from 'axios';
import QRCode from 'react-qr-code';
import InactivityTimer from '../components/InactivityTimer';

const RegistrationSuccess = () => {
    const MAX_SYNC_RETRIES = 3;
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [patientData, setPatientData] = useState(null);
    const [registrationStatus, setRegistrationStatus] = useState('pending'); // pending, processing, success, failed
    const [invoiceUrl, setInvoiceUrl] = useState(null);
    const [externalMRNO, setExternalMRNO] = useState(null);
    const [syncAttempts, setSyncAttempts] = useState(0);
    const [syncErrorMessage, setSyncErrorMessage] = useState('');
    const [isSyncing, setIsSyncing] = useState(false);

    const patientId = searchParams.get('patientId');
    const mrno = searchParams.get('mrno');
    const syncLockKey = patientId ? `hims_sync_lock_${patientId}` : null;
    const isExternalSyncCompleted = Boolean(
        externalMRNO || (patientData?.patient?.externalPatientId && patientData?.patient?.mrno)
    );

    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const updateLocalExternalData = async (externalData) => {
        await axiosInstance.put('/api/patient/update-patient-external-data', {
            patientId: patientId,
            externalPatientId: externalData.patientId || externalData.id || null,
            externalMRNO: externalData.mrno
        });
    };

    const syncPatientWithHims = async (patientResponseData, attempt = 1) => {
        if (!patientResponseData) return false;
        if (isSyncing) return false;
        if (syncLockKey && localStorage.getItem(syncLockKey) === 'in_progress') return false;

        const patient = patientResponseData;
        const patientDataObj = patient.patient;
        const paymentData = patient.payment;

        // Already synced in local DB, never call HIMS again.
        if (patientDataObj?.externalPatientId && patientDataObj?.mrno) {
            setRegistrationStatus('success');
            setExternalMRNO(patientDataObj.mrno);
            setSyncErrorMessage('');
            return true;
        }

        const externalPayload = {
            firstName: patientDataObj?.firstName || '',
            middleName: patientDataObj?.middleName || '',
            lastName: patientDataObj?.lastName || '',
            gender: patientDataObj?.gender || '',
            genderCode: patientDataObj?.genderCode || '',
            nationality: patientDataObj?.nationality || 'Indian',
            emailAddress: patientDataObj?.emailAddress || '',
            contactNumber: patientDataObj?.contactNumber || '',
            whatsappNumber: patientDataObj?.whatsappNumber || '',
            address: patientDataObj?.address || '',
            state: patientDataObj?.state || '',
            city: patientDataObj?.city || '',
            pinCode: patientDataObj?.pinCode || '',
            siteId: '2468',
            age: patientDataObj?.age || null,
            dob: patientDataObj?.dob || null,
            relation: patientDataObj?.relationCode || null,
            prefixName: patientDataObj?.prefixName || null,
            prefix: patientDataObj?.prefix || patientDataObj?.prefixNameText || '',
            agree: patientDataObj?.agree ? 1 : 0,
            receiveCall: patientDataObj?.receiveCall ? 1 : 0,
            base64String: patientDataObj?.base64String || '',
            siteCode: 'PBMH',
            maritalStatus: patientDataObj?.maritalStatus || '',
            maritialStatusCode: patientDataObj?.maritialStatusCode || '',
            emergencyContactNo: patientDataObj?.emergencyContactNo || '',
            emergencyContactName: patientDataObj?.emergencyContactName || '',
            postOffice: patientDataObj?.postOffice || '',
            streetvillage: patientDataObj?.streetvillage || '',
            policeStation: patientDataObj?.policeStation || '',
            district: patientDataObj?.district || '',
            userId: 2,
            locality: patientDataObj?.locality || '',
            ...(patientDataObj?.aadhaarCardNo?.trim() ? { aadhaarCardNo: patientDataObj.aadhaarCardNo } : {}),
            addressList: [{
                address: patientDataObj?.address || '',
                districtName: patientDataObj?.district || patientDataObj?.districtName || '',
                isUrban: patientDataObj?.isUrban !== undefined ? patientDataObj.isUrban : true,
                stateId: patientDataObj?.stateId || null,
                cityId: patientDataObj?.cityId ? String(patientDataObj.cityId) : '',
                countryId: patientDataObj?.countryId || null,
                addressId: patientDataObj?.addressId || null,
                policeStation: patientDataObj?.policeStation || '',
                districtId: patientDataObj?.districtId ? String(patientDataObj.districtId) : '',
                pin: patientDataObj?.pinCode || '',
                cityName: patientDataObj?.city || patientDataObj?.cityName || '',
                stateName: patientDataObj?.state || patientDataObj?.stateName || '',
                isRural: patientDataObj?.isUrban !== undefined ? !patientDataObj.isUrban : false,
                countryName: patientDataObj?.countryName || 'INDIA',
                village: patientDataObj?.streetvillage || '',
                postOffice: patientDataObj?.postOffice || ''
            }],
            payment: {
                paymentStatus: 'success',
                amount: (paymentData?.amount_received || paymentData?.amount_initiated || 100),
                currency: 'INR',
                orderId: paymentData?.razorpayOrderId || paymentData?.orderId || '',
                paymentId: paymentData?.razorpayPaymentId || paymentData?.paymentId || '',
            }
        };

        try {
            const token = localStorage.getItem('token');
            setIsSyncing(true);
            setRegistrationStatus('processing');
            setSyncAttempts(attempt);
            setSyncErrorMessage('');
            if (syncLockKey) localStorage.setItem(syncLockKey, 'in_progress');

            const extRes = await axios.post(
                `${process.env.REACT_APP_KIOSK_OPS_URL}/patient/newRegistration?pay=false`,
                externalPayload,
                {
                    headers: {
                        'Authorization': token ? `Bearer ${token}` : '',
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (extRes.data && (extRes.data.success || extRes.data.mrno)) {
                setRegistrationStatus('success');
                setExternalMRNO(extRes.data.mrno);
                setInvoiceUrl(extRes.data.invoiceUrl);
                await updateLocalExternalData(extRes.data);
                if (syncLockKey) localStorage.removeItem(syncLockKey);
                return true;
            }

            throw new Error('External registration completed but no MRNO received');
        } catch (externalError) {
            const message = externalError.response?.data?.message || externalError.message || 'External sync failed';
            setRegistrationStatus('failed');
            setSyncErrorMessage(message);

            if (attempt < MAX_SYNC_RETRIES) {
                await sleep(attempt * 1500);
                return syncPatientWithHims(patientResponseData, attempt + 1);
            }
            return false;
        } finally {
            setIsSyncing(false);
            if (syncLockKey && localStorage.getItem(syncLockKey) === 'in_progress') {
                localStorage.removeItem(syncLockKey);
            }
        }
    };

    const handleRetrySync = async () => {
        try {
            if (!patientId || isSyncing || isExternalSyncCompleted) return;
            const patientResponse = await axiosInstance.get(`/api/patient/get-patient-data/${patientId}`);
            setPatientData(patientResponse.data);
            await syncPatientWithHims(patientResponse.data, 1);
        } catch (error) {
            setSyncErrorMessage(error.response?.data?.error || 'Unable to retry sync right now');
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (patientId) {
                    const patientResponse = await axiosInstance.get(`/api/patient/get-patient-data/${patientId}`);
                    setPatientData(patientResponse.data);
                    await syncPatientWithHims(patientResponse.data, 1);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        if (patientId) {
            fetchData();
        } else {
            setLoading(false);
        }
    }, [patientId]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <>
            <InactivityTimer />
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-7xl w-full bg-white rounded-2xl shadow-2xl overflow-hidden">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-center">
                        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h1 className="text-3xl font-bold text-white mb-2">Registration Successful!</h1>
                        <p className="text-green-100">Your patient registration has been completed with payment</p>
                    </div>

                    {/* Content */}
                    <div className="p-8">
                        {/* Disclaimer */}
                        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-6 rounded shadow-sm">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-lg font-bold text-yellow-800">
                                        Please Collect your Registration Card From Registration Counter.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Two Column Layout: Patient Details Left, QR Code Right */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                            {/* Left Side - Patient Information */}
                            <div className="lg:col-span-1">
                                <div className="bg-gray-50 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                        <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                        Patient Details
                                    </h3>
                                    {patientData ? (
                                        <div className="space-y-2 text-sm">
                                            <p><span className="font-medium text-gray-600">Name:</span> {patientData.patient?.prefixNameText || patientData.prefixNameText} {patientData.patient?.firstName || patientData.firstName} {patientData.patient?.middleName || patientData.middleName} {patientData.patient?.lastName || patientData.lastName}</p>
                                            <p><span className="font-medium text-gray-600">MR No:</span> <span className="font-mono bg-white px-2 py-1 rounded">{externalMRNO}</span></p>
                                            <p><span className="font-medium text-gray-600">Contact:</span> {patientData.patient?.contactNumber || patientData.contactNumber}</p>
                                            <p><span className="font-medium text-gray-600">Email:</span> {patientData.patient?.emailAddress || patientData.emailAddress || 'N/A'}</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-2 text-sm">
                                            <p><span className="font-medium text-gray-600">MR No:</span> <span className="font-mono bg-white px-2 py-1 rounded">{mrno}</span></p>
                                            <p><span className="font-medium text-gray-600">Patient ID:</span> {patientId}</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Middle - Payment Information */}
                            <div className="lg:col-span-1">
                                <div className="bg-gray-50 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                        <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                        Payment Details
                                    </h3>
                                    <div className="space-y-2 text-sm">
                                        <p><span className="font-medium text-gray-600">Registration Fee:</span> <span className="text-green-600 font-semibold">₹100</span></p>
                                        <p><span className="font-medium text-gray-600">Status:</span> <span className="text-green-600 font-semibold">Paid</span></p>
                                        {patientData?.payment && (
                                            <>
                                                <p><span className="font-medium text-gray-600">Payment ID:</span> <span className="font-mono text-xs">{patientData.payment.paymentId}</span></p>
                                                <p><span className="font-medium text-gray-600">Order ID:</span> <span className="font-mono text-xs">{patientData.payment.orderId}</span></p>
                                            </>
                                        )}
                                        <div className="mt-3 pt-2 border-t border-gray-200">
                                            <p className="flex items-center">
                                                <strong>HIS Registration:</strong>
                                                <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${registrationStatus === 'success'
                                                    ? 'bg-green-100 text-green-800'
                                                    : registrationStatus === 'failed'
                                                        ? 'bg-red-100 text-red-800'
                                                        : registrationStatus === 'processing'
                                                            ? 'bg-blue-100 text-blue-800'
                                                            : 'bg-yellow-100 text-yellow-800'
                                                    }`}>
                                                    {registrationStatus === 'success'
                                                        ? 'Completed'
                                                        : registrationStatus === 'failed'
                                                            ? 'Failed'
                                                            : registrationStatus === 'processing'
                                                                ? 'Processing...'
                                                                : 'Pending'}
                                                </span>
                                            </p>
                                            {registrationStatus === 'failed' && (
                                                <div className="mt-3">
                                                    <p className="text-sm text-red-600 mb-2">
                                                        Sync failed{syncErrorMessage ? `: ${syncErrorMessage}` : '.'}
                                                    </p>
                                                    <p className="text-xs text-gray-500 mb-2">
                                                        Attempted: {syncAttempts}/{MAX_SYNC_RETRIES}
                                                    </p>
                                                    <button
                                                        onClick={handleRetrySync}
                                                        disabled={isSyncing || isExternalSyncCompleted}
                                                        className={`px-4 py-2 rounded-lg text-sm font-semibold text-white ${(isSyncing || isExternalSyncCompleted) ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                                                            }`}
                                                    >
                                                        {isExternalSyncCompleted ? 'Synced' : (isSyncing ? 'Retrying...' : 'Retry Sync')}
                                                    </button>
                                                </div>
                                            )}
                                            {/* {externalMRNO && (
                                                <p className="mt-1"><strong>MRNO:</strong> <span className="text-blue-600">{externalMRNO}</span></p>
                                            )} */}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Side - QR Code Section */}
                            <div className="lg:col-span-1">
                                {invoiceUrl && registrationStatus === 'success' ? (
                                    <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm h-fit">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center justify-center">
                                            <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                                            </svg>
                                            Invoice QR Code
                                        </h3>
                                        <div className="flex flex-col items-center">
                                            <div className="bg-gray-50 p-4 rounded-lg shadow-sm border">
                                                <QRCode
                                                    value={invoiceUrl}
                                                    size={150}
                                                    level="M"
                                                    includeMargin={true}
                                                />
                                            </div>
                                            <p className="text-sm text-gray-600 mt-3 text-center">Scan QR code to download invoice</p>
                                            <button
                                                onClick={() => window.open(invoiceUrl, '_blank')}
                                                className="mt-3 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition duration-200 flex items-center"
                                            >
                                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                Download Invoice
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-gray-100 rounded-lg p-6 border border-gray-200 h-fit flex flex-col items-center justify-center">
                                        <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                                        </svg>
                                        <h3 className="text-md font-semibold text-gray-600 mb-2">Invoice QR Code</h3>
                                        <p className="text-gray-500 text-sm text-center">
                                            {registrationStatus === 'processing'
                                                ? 'QR Code will appear after successful registration'
                                                : registrationStatus === 'failed'
                                                    ? 'Registration failed - QR code not available'
                                                    : 'QR Code will be generated after registration'}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Next Steps */}
                        <div className="bg-blue-50 rounded-lg p-6 mb-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                What's Next?
                            </h3>
                            <div className="text-sm text-gray-700 space-y-2">
                                <p>✅ Your patient registration is complete</p>
                                <p>✅ Registration fee of ₹100 has been paid</p>
                                <p>📋 You can now book consultations with doctors</p>
                                <p>🏥 Visit the hospital with your MR Number for any services</p>
                            </div>
                        </div>


                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={() => navigate('/')}
                                className="px-8 py-3 bg-green-600 text-white font-semibold rounded-lg shadow-lg hover:bg-green-700 transition duration-200"
                            >
                                Back to Home
                            </button>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="bg-gray-100 p-4 text-center text-sm text-gray-600">
                        <p>Keep your MR Number safe for future visits</p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default RegistrationSuccess;